import * as StateLib from "@/Utils/StateLib";
import { GetWSMgr } from "@/Utils/StateLib";
import { isNullish } from "@theorgchartdev/storybook";
import { kAdminUser, kRWUser, kROUser } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QUserMgmtRFC } from "@coreapp/CoreApp/QUserMgmtRFC";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { appstrp } from "@coreapp/utils/appstrp";
import { appstr } from "@coreapp/utils/appstr";
import { CRC32 } from "@coreapp/utils/CRC32";
import { GetSupportContact } from "@coreapp/utils/GetSupportContact";
import { ValidateEmailAddr } from "@coreapp/utils/ValidateEmailAddr";

export const GetNewUserObject = security => {
  let defaultGroup = "0"; //Full access
  if (security["userAssignment"] && security["userAssignment"]["defaultGroup"]) {
    defaultGroup = security["userAssignment"]["defaultGroup"];
  }
  return { username: "", group: defaultGroup, role: kROUser };
};

export const sendUserUpdate = async ({
  isNewUser,
  editUser,
  theData,
  usersList,
  afterSaveHandler
}) => {
  const { result } = await UpdateUser({
    isNewUser,
    editUser,
    theData,
    usersList,
    afterSaveHandler
  });
  if (result["success"] === true) afterSaveHandler();
  return { result };
};

export function UpdateUser({
  isNewUser = false,
  editUser,
  theData,
  usersList = [],
  afterSaveHandler
}) {
  return new Promise((resolve, reject) => {
    const wsMgr = StateLib.GetWSMgr();
    if (!wsMgr) {
      reject();
      return;
    }

    const mode = isNewUser ? "addUser" : "updateUser";

    const accountName = wsMgr.GetAccountName();
    const user = wsMgr.GetUserName();

    const syncRFC = new QUserMgmtRFC();
    const callbackObject = {
      ChangeUserInfoResult: result => {
        const updateSuccessful = result["success"];
        if (updateSuccessful) {
          const updatedUser = result["updatedUser"];
          if (updatedUser) wsMgr.UpdateUserInfo(updatedUser);
        } else {
          try {
            handleUserUpdateFailure({
              isNewUser,
              result,
              editUser,
              theData,
              usersList,
              afterSaveHandler
            });
          } catch (e) {
            console.log("Error", e);
          }
        }
        resolve({ result });
      }
    };

    syncRFC.InitUserMgmtRFC(wsMgr, callbackObject);
    syncRFC.SetParams(accountName, user, mode, JSON.stringify(theData));
    Globals._rfcMgr.QueueRFC(syncRFC);
  });
}

export const handleUserUpdateFailure = ({
  result,
  editUser,
  theData,
  usersList,
  afterSaveHandler,
  isNewUser
}) => {
  const wsMgr = GetWSMgr();
  let msg = appstr("orginfochgfail");

  switch (result["code"]) {
    case "0001":
      msg = appstr("useradderr");
      break;
    case "0002": {
      const existingEmailUser =
        typeof editUser.email === "string" && editUser.email !== ""
          ? usersList.find(
              user =>
                typeof user.email === "string" &&
                user.email.toLowerCase() === editUser.email.toLowerCase()
            )
          : undefined;
      if (existingEmailUser) {
        const alert = new QAlert();
        alert.InitAlert(
          appstr("Alert"),
          `"${editUser.email}" ${appstr("delegatedsameorgemail")}`,
          null,
          wsMgr
        );
      } else {
        wsMgr.CreateReactAlert(
          appstr("Alert"),
          appstr("confirmAddDelegated"),
          () => {
            theData["doAssignDelegate"] = true;
            sendUserUpdate({
              theData,
              isNewUser,
              afterSaveHandler,
              editUser,
              usersList
            });
          },
          () => {
            theData["doAssignDelegate"] = false;
          },
          null,
          null
        );
      }
      // alert handled
      return;
    }
    case "0003":
      msg = appstr("unexpectederr");
      break;
    case "0004":
      msg = "'" + editUser.email + "'" + appstr("delegatedsameorgemail");
      break;
  }

  if (msg.length > 0) {
    const UserFailAlert = new QAlert();
    UserFailAlert.InitCodeAlert(appstr("Alert"), msg, null, wsMgr, "CancelAlert", "OK");
  }
};

export const IsValidUser = (editUser, clonedUser, users, groups) => {
  const wsMgr = StateLib.GetWSMgr();
  const curUser = wsMgr.GetUserName();

  const targetUser = clonedUser?.username;
  const curSeat = clonedUser?.role;

  const curUserName = editUser?.username;
  const curEmail = editUser?.email;
  const targetSeat = editUser?.role;
  const curRoleAssignmentSeat = groups?.[clonedUser.group]?.roleassignment ?? curSeat;
  const targetRoleAssignmentSeat = groups?.[editUser.group]?.roleassignment;

  const results = { valid: false, alert: "", errorFields: [], overlimit: false };

  const incompleteAlert = appstrp("One or more fields contain errors", ".");
  const overLimitAlert = appstr("You are over your seat limit");

  if (curUser == targetUser && curSeat != kAdminUser) {
    results.alert = appstr("curusersysadmin");
  }

  let showIncompleteAlert = false;
  let showOverLimit = false;

  if (!isNullish(curEmail) && curEmail.length > 0 && !ValidateEmailAddr(curEmail)) {
    results.errorFields.push({ message: appstr("invalidemail"), type: "email" });
    showIncompleteAlert = true;
  }

  if (curUserName.length < 3) {
    results.errorFields.push({ message: appstr("validmongouserid"), type: "username" });
    showIncompleteAlert = true;
  }

  if (showIncompleteAlert) {
    results.valid = false;
    results.alert = incompleteAlert;
    return results;
  }

  if (targetSeat !== kROUser) {
    if (!CanAddSeats(users, curSeat, groups)) {
      results.errorFields.push({ message: appstr("maxseats"), type: "role" });
      showOverLimit = true;
    }
  } else if ([kAdminUser, kRWUser].includes(targetRoleAssignmentSeat)) {
    if (!CanAddSeats(users, curRoleAssignmentSeat, groups)) {
      results.errorFields.push({ message: appstr("maxseats"), type: "role" });
      showOverLimit = true;
    }
  }

  if (showOverLimit) {
    results.valid = false;
    results.alert = overLimitAlert;
    results.supportContact = GetSupportContact();
    return results;
  }

  results.valid = true;

  return results;
};

export const CanAddSeats = (users, curSeat, groups) => {
  if (!users) return false;
  const wsMgr = GetWSMgr();
  if (!wsMgr) return false;
  const oi = wsMgr.fOrgInfo;
  const seatCount = parseInt(oi.GetSeatCount());
  const usedSeats = GetUsedSeats(users, groups);
  if ([kRWUser, kAdminUser].includes(curSeat)) return true;
  return usedSeats < seatCount;
};

export const GetUsedSeats = (users, groups) => {
  var c = 0;
  var userList = users;
  for (var userIdx in userList) {
    const user = userList[userIdx];
    const userRole = user.role;
    const roleAssigned = groups?.[user.group]?.roleassignment;
    if (userRole !== kROUser || [kRWUser, kAdminUser].includes(roleAssigned)) c++;
  }
  return c;
};

export const GetUserRole = uTypeCode => {
  let userType = "";
  switch (uTypeCode) {
    case kROUser:
      userType = "readonly";
      break;
    case kRWUser:
      userType = "readwrite";
      break;
    case kAdminUser:
      userType = "adminuser";
      break;
  }
  return userType;
};

export const impersonateUser = targetUser => {
  const wsmgr = StateLib.GetWSMgr();
  const adminUserInfo = wsmgr.GetUserInfo();
  const origin = window.location.origin;
  const endPoint = "/admin-impersonate?";
  let params = `adminuser=${adminUserInfo.GetUserEmail()}&user=${targetUser.email}`;
  const crc = CRC32.str(params);
  params = `${params}&crc=${crc}`;
  const dl = "dl=" + window.btoa(params).replace(/=/g, "");
  var fullURL = origin + endPoint + dl;

  window.open(fullURL);
};

export const GetDisplayName = user => {
  const displayName = user?.name || user?.email || user?.username;
  return user?.delegatedEmail ? `${user.delegatedEmail} (${appstr("Delegated")})` : displayName;
};

export const userTypes = {
  [kAdminUser]: "adminuser",
  [kRWUser]: "readwrite",
  [kROUser]: "readonly"
};
